import { useEffect, useState } from "react";
import { checkin, getUncheckedParticipantsLogs } from "./apiRoutes/events";
import {
  Loader2,
  TriangleAlert,
  CircleCheckBig,
  X,
  CheckCircle,
} from "lucide-react";
import { ClipLoader } from "react-spinners";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Sidebar from "../../customComponents/Sidebar/Sidebar";

const query = new URLSearchParams(window.location.search);

/**
 * One QR admits the whole party, so the volunteer needs the head count.
 */
const formatParty = ({ adult_count, child_count, party_size }) => {
  const parts = [];

  if (adult_count) {
    parts.push(`${adult_count} adult${adult_count === 1 ? "" : "s"}`);
  }

  if (child_count) {
    parts.push(`${child_count} child${child_count === 1 ? "" : "ren"}`);
  }

  if (parts.length > 0) {
    return `Admitting ${parts.join(" and ")}`;
  }

  if (party_size) {
    return `Admitting ${party_size} ${party_size === 1 ? "person" : "people"}`;
  }

  return "Admitting 1 person";
};

function EventAttendance() {
  const [key, setKey] = useState(query.get("key"));
  const [eventId, setEventId] = useState(query.get("id"));
  const [userId, setUserId] = useState(query.get("user"));
  const [attendanceCode, setAttendanceCode] = useState("");
  const [isFinished, setIsFinished] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [participantCodes, setParticipantCodes] = useState([]);
  const [registration, setRegistration] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const isAdmin = !!tmtAttendance.is_admin;
  const isEventVolunteer = !!tmtAttendance.is_staff;
  const canScan = isAdmin || isEventVolunteer;

  /*
   * A fallback only: the shortcode redirects unauthorised visitors before this
   * ever loads. It used to send them to the relative URL "portal-login" with
   * no explanation of what went wrong; now they get the page that says whether
   * they need to sign in or need the volunteer role, and carries the scanned
   * link so they can be returned to it.
   */
  if (!canScan) {
    const target = new URL(tmtAttendance.no_access, window.location.origin);
    target.searchParams.set("return_to", window.location.href);
    window.location.replace(target.toString());

    return null;
  }

  const [completeDetails, setCompleteDetails] = useState(true);
  useEffect(() => {
    if (key && eventId && userId) {
      handleCheckinEvent();
    } else {
      setCompleteDetails(false);
    }
    initializeEventLogs();
  }, []);

  const handleStartLoading = () => {
    setIsLoading(true);
  };
  const handleStopLoading = () => {
    setIsLoading(false);
  };

  const handleCheckinEvent = async () => {
    handleStartLoading();
    setErrorMessage("");
    setRegistration(null);

    try {
      const response = await checkin(userId, eventId, key, attendanceCode);

      if (response?.status === "error") {
        setErrorMessage(response.message || "This code could not be checked in.");
      } else {
        setRegistration(response?.registration ?? null);
      }
    } catch (error) {
      setErrorMessage("Could not reach the server. Please try again.");
    } finally {
      handleStopLoading();
      setIsFinished(true);
    }
  };

  const handleReset = () => {
    setIsFinished(false);
    setKey("");
    setEventId("");
    setUserId("");
    setRegistration(null);
    setErrorMessage("");
    setParticipantCodes((prev) =>
      prev.filter((val) => val.label !== attendanceCode)
    );
    setAttendanceCode("");
    setCompleteDetails(false);
  };

  const initializeEventLogs = async () => {
    const logs = await getUncheckedParticipantsLogs();
    const participants = logs.map((val) => ({
      label: val.code,
    }));
    setParticipantCodes(participants);
  };

  const loader = (
    <ClipLoader
      color="#1a73e8" // hex works
      size={140} // size in px
      cssOverride={{
        borderWidth: "8px", // default is 2-3px — increase to make it thicker
      }}
    />
  );

  const inputs = (
    <div className="input-container">
      <div>
        <p style={{ fontWeight: "bold", fontSize: "1.5rem" }}>
          Attendance Code
        </p>
        {/* <input
          type="text"
          value={attendanceCode}
          onChange={(e) => {
            setAttendanceCode(e.target.value);
          }}
        /> */}
        <Autocomplete
          disablePortal
          options={participantCodes}
          inputValue={attendanceCode}
          onInputChange={(event, newInputValue) => {
            console.log(newInputValue);
            setAttendanceCode(newInputValue);
          }}
          sx={{
            // width: 300,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "#f0f6ff",
              "& fieldset": {
                borderColor: "#2196f3", // blue border
              },
              "&:hover fieldset": {
                borderColor: "#1976d2", // darker blue on hover
              },
              "&.Mui-focused fieldset": {
                borderColor: "#1565c0", // even darker blue when focused
                boxShadow: "0 0 0 2px rgba(33, 150, 243, 0.2)",
              },
            },
            "& .MuiInputLabel-root": {
              color: "#1976d2",
            },
            "& .MuiAutocomplete-popupIndicator": {
              color: "#1976d2",
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                "& .MuiOutlinedInput-input": {
                  border: "none",
                },
              }}
            />
          )}
        />
      </div>
      {/* <button onClick={handleCheckinEvent}>Submit</button> */}
      <button
        className={`ButtonW100 wpem-theme-button`}
        onClick={handleCheckinEvent}
        style={{ height: "3rem" }}
      >
        Submit
      </button>
    </div>
  );

  return (
    <div className="admin-portal-container">
      <Sidebar selectedIndex={1} />
      <div className="event-attendance-container">
        {isFinished ? (
          <div>
            <div className="finished-label">
              {errorMessage ? (
                <TriangleAlert size={40} color="#c62828" />
              ) : (
                <CheckCircle size={40} color="#1a73e8" />
              )}
              <p>{errorMessage ? "Check-in Failed" : "Attendance Logged"}</p>
            </div>

            {errorMessage ? (
              <p className="checkin-error">{errorMessage}</p>
            ) : (
              registration && (
                <div className="checkin-summary">
                  <p className="checkin-name">{registration.name}</p>

                  {/*
                    * Stated either way rather than shown only for walk-ins.
                    * At the door the volunteer needs to know which this is,
                    * not infer it from a missing badge.
                    */}
                  {/*
                    * Three badges now, so they sit in a wrapping row rather
                    * than stacking down the card. Returning and TMT customer
                    * are the loud ones: a first-timer who is not a customer
                    * is the ordinary case, so the exceptions are what should
                    * catch a volunteer's eye.
                    */}
                  <div className="checkin-tags">
                    <span
                      className={`checkin-type ${
                        registration.is_walk_in
                          ? "checkin-type--walk-in"
                          : "checkin-type--pre"
                      }`}
                    >
                      {registration.is_walk_in ? "Walk-in" : "Pre-registered"}
                    </span>

                    {registration.is_first_time === true && (
                      <span className="checkin-type checkin-type--yes">
                        First time: Yes
                      </span>
                    )}
                    {registration.is_first_time === false && (
                      <span className="checkin-type checkin-type--no">
                        First time: No
                      </span>
                    )}
                    {registration.is_first_time === null && (
                      <span className="checkin-type checkin-type--undefined">
                        First time: Undefined
                      </span>
                    )}

                    {registration.is_tmt_customer === true && (
                      <span className="checkin-type checkin-type--yes">
                        TMT customer: Yes
                      </span>
                    )}
                    {registration.is_tmt_customer === false && (
                      <span className="checkin-type checkin-type--no">
                        TMT customer: No
                      </span>
                    )}
                    {registration.is_tmt_customer === null && (
                      <span className="checkin-type checkin-type--undefined">
                        TMT customer: Undefined
                      </span>
                    )}
                  </div>

                  {registration.was_checked_in && (
                    <p className="checkin-warning">
                      Already checked in earlier — please verify before admitting.
                    </p>
                  )}

                  <p className="checkin-party">
                    {formatParty(registration)}
                  </p>
                  <p className="checkin-code">Code: {registration.code}</p>
                  {registration.phone && (
                    <p className="checkin-phone">Phone: {registration.phone}</p>
                  )}
                </div>
              )
            )}

            <div>
              <button
                className={`ButtonW100 wpem-theme-button`}
                onClick={handleReset}
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  height: "3rem",
                }}
              >
                Log Another
              </button>
            </div>
          </div>
        ) : isLoading ? (
          loader
        ) : !completeDetails ? (
          inputs
        ) : (
          <div className=""></div>
        )}
      </div>
    </div>
  );
}

export default EventAttendance;
